import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import {Add as AddIcon, ChevronRight as ChevronRightIcon} from "@mui/icons-material";
import {Gauge, LayoutDashboard, Settings2} from "lucide-react";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {PageHeader} from "../../shared/ui";
import {getPendencias} from "./SistemasService";
import {getBackendEntityMessage} from "./sistemasUtils";
import {listarTudo} from "./useSistemaPagina";
import {plural} from "./sistemaPagina";
import NovoSistemaDialog from "./components/NovoSistemaDialog";
import {FonteChip, SistemaLogo, StatusChip} from "./components/SistemaVisual";

// Sistemas com o total de pendências de cada um. As pendências chegam depois e, se falharem, o cartão fica sem elas.
const useSistemasLista = () => {
    const [estado, setEstado] = useState({loading: true, erro: '', sistemas: [], pendencias: {}});

    const carregar = useCallback(async () => {
        setEstado((atual) => ({...atual, loading: true, erro: ''}));
        try {
            const sistemas = await listarTudo('sistemas', {sortBy: 'nome', order: 'asc'});
            setEstado({loading: false, erro: '', sistemas, pendencias: {}});
            const totais = await Promise.allSettled(sistemas.map((sistema) => getPendencias(sistema.id)));
            setEstado((atual) => ({
                ...atual,
                pendencias: Object.fromEntries(sistemas.map((sistema, i) => [
                    sistema.id,
                    totais[i].status === 'fulfilled' ? (totais[i].value?.data?.valor?.total || null) : null,
                ])),
            }));
        } catch (error) {
            setEstado({loading: false, erro: getBackendEntityMessage(error, 'Não foi possível carregar os sistemas.'), sistemas: [], pendencias: {}});
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    return {...estado, recarregar: carregar};
};

const Numero = ({valor, rotulo}) => (
    <Paper variant="outlined" sx={{px: 2, py: 1.5, minWidth: 140, flex: {xs: '1 1 140px', md: '0 0 auto'}}}>
        <Typography variant="h6" fontWeight={700}>{valor}</Typography>
        <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
    </Paper>
);

const Meta = ({valor, rotulo}) => (
    <Box>
        <Typography variant="subtitle1" fontWeight={700} component="div">{valor}</Typography>
        <Typography variant="caption" color="text.secondary">{rotulo}</Typography>
    </Box>
);

const CartaoDoSistema = ({sistema, total}) => (
    <Card variant="outlined" sx={{height: '100%'}}>
        <CardActionArea component={RouterLink} to={`/sistemas/${sistema.id}`} sx={{height: '100%', display: 'flex', alignItems: 'stretch'}}>
            <CardContent sx={{display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%'}}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <SistemaLogo sistema={sistema} />
                    <Box sx={{flexGrow: 1, minWidth: 0}}>
                        <Typography variant="subtitle1" fontWeight={700} component="h3" sx={{overflowWrap: 'anywhere'}}>{sistema.nome}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace'}}>chave {sistema.chave}</Typography>
                    </Box>
                    <StatusChip status={sistema.status} />
                </Stack>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 40}}
                >
                    {sistema.descricao || 'Sem descrição.'}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{minHeight: 24}}>
                    <FonteChip sistema={sistema} />
                    {total?.bloqueia ? <Chip size="small" color="error" label={plural(total.bloqueia, 'pendência', 'pendências')} /> : null}
                    {total?.construcao ? <Chip size="small" color="warning" variant="outlined" label={`${total.construcao} em construção`} /> : null}
                    {total?.aviso ? <Chip size="small" color="info" variant="outlined" label={plural(total.aviso, 'aviso', 'avisos')} /> : null}
                    {total && !total.bloqueia && !total.construcao && !total.aviso ? <Chip size="small" color="success" variant="outlined" label="Nada pendente" /> : null}
                </Stack>
                {sistema.status === 'DESENVOLVIMENTO' ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'primary.main'}}>
                        <Typography variant="body2" fontWeight={600}>Continuar o cadastro</Typography>
                        <ChevronRightIcon fontSize="small" />
                    </Stack>
                ) : null}
                <Box sx={{mt: 'auto', pt: 1.5, borderTop: 1, borderColor: 'divider', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1}}>
                    <Meta valor={(sistema.modulos || []).length} rotulo="módulos" />
                    <Meta valor={sistema.sigla || '—'} rotulo="sigla" />
                </Box>
            </CardContent>
        </CardActionArea>
    </Card>
);

export default function SistemasPage({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(openParent);
    const [novoAberto, setNovoAberto] = useState(false);
    const {loading, erro, sistemas, pendencias, recarregar} = useSistemasLista();
    const contar = (status) => sistemas.filter((sistema) => sistema.status === status).length;

    const menus = [
        {label: "Dashboard", id: "dashboard", selected: false, disabled: false, icon: <LayoutDashboard size={20} />, onClick: () => navigate("/")},
        {label: "Sistemas", id: "sistemas", selected: true, disabled: false, icon: <Settings2 size={20} />, onClick: () => navigate("/sistemas")},
        {label: "Métricas", id: "metricas", selected: false, disabled: false, icon: <Gauge size={20} />, onClick: () => navigate("/metricas")},
    ];

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader
                title="Sistemas"
                subtitle=""
                titleAction={() => navigate("/")}
                itemsMenu={menus}
                onHandleMenuOpen={() => null}
                onHandleDrawerToggle={setOpen}
                openParent={open}
            />

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: {xs: 2, md: 6},
                    minWidth: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    mt: 8,
                    backgroundColor: theme.palette.background.default,
                    minHeight: 'calc(100dvh - 64px)',
                }}
            >
                <Stack spacing={3}>
                    <Stack direction={{xs: 'column', md: 'row'}} spacing={2} justifyContent="space-between" alignItems={{md: 'center'}}>
                        <Box>
                            <Typography variant="h4" component="h1" fontWeight={700}>Sistemas</Typography>
                            <Typography variant="body1" color="text.secondary">
                                O que a Busifarm vende e opera. Cada sistema tem uma página só, com o catálogo de módulos, funcionalidades e endpoints, as pendências e a integração.
                            </Typography>
                        </Box>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNovoAberto(true)} sx={{flexShrink: 0}}>Novo sistema</Button>
                    </Stack>

                    {erro ? <Alert severity="error">{erro}</Alert> : null}

                    {loading ? (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{py: 6}}>
                            <CircularProgress size={20} />
                            <Typography color="text.secondary">Carregando os sistemas…</Typography>
                        </Stack>
                    ) : (
                        <>
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                <Numero valor={contar('VENDA')} rotulo="sistemas à venda" />
                                <Numero valor={contar('DESENVOLVIMENTO')} rotulo="em desenvolvimento" />
                                <Numero valor={contar('INTERNO')} rotulo="de uso interno" />
                            </Stack>
                            {sistemas.length ? (
                                <Box sx={{display: 'grid', gap: 2, gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))'}}}>
                                    {sistemas.map((sistema) => <CartaoDoSistema key={sistema.id} sistema={sistema} total={pendencias[sistema.id]} />)}
                                </Box>
                            ) : (
                                <Paper variant="outlined" sx={{p: 4, textAlign: 'center'}}>
                                    <Typography color="text.secondary">Nenhum sistema cadastrado.</Typography>
                                </Paper>
                            )}
                        </>
                    )}
                </Stack>

                <NovoSistemaDialog
                    aberto={novoAberto}
                    onFechar={() => setNovoAberto(false)}
                    onCriado={(sistema) => {
                        setNovoAberto(false);
                        if (sistema?.id) navigate(`/sistemas/${sistema.id}/catalogo`, {state: {novoModulo: true}});
                        else recarregar();
                    }}
                />
            </Box>
        </Box>
    );
}
